import { useRef, useState, type ReactNode } from 'react';
import { Info } from 'lucide-react';

interface MetricHintProps {
  label: string;
  help: string;
  children: ReactNode;
  testId?: string;
}

/**
 * Ayuda contextual de una métrica (hover + foco de teclado).
 *
 * El globo se posiciona en `fixed` para no quedar recortado por el
 * `overflow-x-auto` de la tabla; mantiene `title` como fallback nativo.
 */
export function MetricHint({ label, help, children, testId }: MetricHintProps) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const ref = useRef<HTMLSpanElement>(null);

  const show = () => {
    const rect = ref.current?.getBoundingClientRect();
    if (rect) {
      setPosition({
        top: Math.max(8, Math.min(rect.bottom + 6, window.innerHeight - 160)),
        left: Math.max(8, Math.min(rect.left, window.innerWidth - 296)),
      });
    }
    setOpen(true);
  };
  const hide = () => setOpen(false);

  return (
    <span
      ref={ref}
      className="inline-flex items-center gap-1"
      tabIndex={0}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      title={help}
      aria-label={`${label}: ${help}`}
      data-testid={testId}
    >
      {children}
      <Info className="h-3.5 w-3.5 shrink-0 text-gray-400" aria-hidden="true" />
      {open && position && (
        <span
          role="tooltip"
          className="fixed z-50 w-72 rounded-md border bg-card p-2 text-left text-xs font-normal text-gray-700 shadow-lg dark:text-gray-200"
          style={{ top: position.top, left: position.left }}
        >
          <span className="mb-1 block font-semibold">{label}</span>
          {help}
        </span>
      )}
    </span>
  );
}
